/**
 * Plan de Cuentas API Service
 * Servicio para interactuar con la API del Plan de Cuentas Contable
 */

import api from './api';

// Derivar URL base para Contabilidad desde la baseURL actual de `api`
// Si api.baseURL es '/api/content', entonces API_BASE_URL debe ser '/api/contabilidad'
const API_BASE_URL = (() => {
  const baseURL = api?.defaults?.baseURL || '/api/content';
  // Remover '/content' y agregar '/contabilidad'
  return baseURL.replace(/\/content\/?$/i, '/contabilidad');
})();

const planCuentasService = {
  // ============================================
  // CRUD Básico
  // ============================================

  /**
   * Obtener todas las cuentas (vista plana)
   * @param {Object} filters - Filtros opcionales (cuenta_id, tipo_cuenta, activo, nivel)
   */
  getAll: (filters = {}) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/`, {
      params: filters,
    });
  },

  /**
   * Obtener detalle de una cuenta
   * @param {number} id - ID de la cuenta
   */
  getById: (id) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/${id}/`);
  },

  /**
   * Crear nueva cuenta
   * @param {Object} data - Datos de la cuenta
   */
  create: (data) => {
    return api.post(`${API_BASE_URL}/plan-cuentas/`, data);
  },

  /**
   * Actualizar cuenta existente
   * @param {number} id - ID de la cuenta
   * @param {Object} data - Datos a actualizar
   */
  update: (id, data) => {
    return api.patch(`${API_BASE_URL}/plan-cuentas/${id}/`, data);
  },

  /**
   * Eliminar cuenta
   * @param {number} id - ID de la cuenta
   */
  delete: (id) => {
    return api.delete(`${API_BASE_URL}/plan-cuentas/${id}/`);
  },

  // ============================================
  // Endpoints Especiales
  // ============================================

  /**
   * Obtener árbol completo de cuentas
   * @param {number} cuentaId - ID de la cuenta
   * @param {boolean} soloActivos - Filtrar solo cuentas activas
   */
  getTree: (cuentaId, soloActivos = true) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/tree/`, {
      params: {
        cuenta_id: cuentaId,
        solo_activos: soloActivos,
      },
    });
  },

  /**
   * Obtener solo cuentas raíz (nivel 1)
   * @param {number} cuentaId - ID de la cuenta
   */
  getRoots: (cuentaId) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/roots/`, {
      params: {
        cuenta_id: cuentaId,
      },
    });
  },

  /**
   * Obtener hijos directos de una cuenta
   * @param {number} id - ID de la cuenta padre
   * @param {boolean} soloActivos - Filtrar solo activos
   */
  getChildren: (id, soloActivos = true) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/${id}/children/`, {
      params: {
        solo_activos: soloActivos,
      },
    });
  },

  /**
   * Obtener ancestros de una cuenta
   * @param {number} id - ID de la cuenta
   */
  getAncestors: (id) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/${id}/ancestors/`);
  },

  /**
   * Obtener todos los descendientes de una cuenta
   * @param {number} id - ID de la cuenta padre
   * @param {boolean} soloActivos - Filtrar solo activos
   */
  getDescendants: (id, soloActivos = true) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/${id}/descendants/`, {
      params: {
        solo_activos: soloActivos,
      },
    });
  },

  /**
   * Obtener cuentas agrupadas por tipo
   * @param {number} cuentaId - ID de la cuenta
   */
  getPorTipo: (cuentaId) => {
    return api.get(`${API_BASE_URL}/plan-cuentas/por-tipo/`, {
      params: {
        cuenta_id: cuentaId,
      },
    });
  },

  // ============================================
  // Configuración
  // ============================================

};

export default planCuentasService;
